import { Alert, Card, Col, ConfigProvider, DatePicker, Divider, Form, Row, Statistic, Typography, Input } from "antd";
import SDIModal from "../../../../components/ui/SDIModal";
import PropTypes from "prop-types";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../../../variables/api/axiosInstance";
import AvenantAgencySelector from "./AvenantAgencySelector";

const { Text, Title } = Typography;
const { TextArea } = Input;

const ReportEffet = (props) => {
    const [modalReportEffet, setModalReportEffet] = useState(props.open);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    // États pour les données du contrat et du report
    const [contratActuel, setContratActuel] = useState(null);
    const [nouvelleDateEffet, setNouvelleDateEffet] = useState(null);
    const [nouvelleDateEcheance, setNouvelleDateEcheance] = useState(null);
    const [ancienneDateEffet, setAncienneDateEffet] = useState(null);
    const [ancienneDateEcheance, setAncienneDateEcheance] = useState(null);
    const [dureeInitiale, setDureeInitiale] = useState(0);
    const [fraisAccessoires, setFraisAccessoires] = useState(0); // Frais fixes selon les spécifications

    useEffect(() => {
        setModalReportEffet(props.open);
        if (props.open && props?.contrat?.codeId) {
            console.log("Contrat pour report d'effet:", props?.contrat);
            setContratActuel(props?.contrat);

            // Définir les dates initiales
            const dateEffetInitiale = dayjs(props?.contrat?.dateEffet);
            const dateEcheanceInitiale = dayjs(props?.contrat?.dateEcheance);

            setAncienneDateEffet(dateEffetInitiale);
            setAncienneDateEcheance(dateEcheanceInitiale);

            // Calculer la durée initiale du contrat
            const duree = dateEcheanceInitiale.diff(dateEffetInitiale, 'month', true);
            setDureeInitiale(duree);

            // Nouvelle date d'effet par défaut : le lendemain de la date actuelle
            const nouvelleDate = dayjs().add(1, 'day');
            setNouvelleDateEffet(nouvelleDate);

            // Calculer automatiquement la nouvelle échéance
            const nouvelleEcheance = nouvelleDate.add(duree, 'month').subtract(1, 'day').endOf('day');
            setNouvelleDateEcheance(nouvelleEcheance);

            axiosInstance.get(`/api/avenant/accessoires/RE/${props?.contrat?.engins[0]?.categorie?.codeCategorie}`)
                .then(response => {
                    setFraisAccessoires(response.data);
                })
                .catch(error => {
                    console.error("Erreur lors de la récupération des frais accessoires:", error);
                });
        }
    }, [props.open, props?.contrat?.codeId]);

    const { DollarOutlined, WarningOutlined } = require('@ant-design/icons');

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(value);
    };

    const handleNouvelleDateEffetChange = (date) => {
        setNouvelleDateEffet(date);

        // Recalculer automatiquement la nouvelle échéance en maintenant la durée initiale
        if (date && dureeInitiale) {
            const nouvelleEcheance = date.add(dureeInitiale, 'month').subtract(1, 'day').endOf('day');
            setNouvelleDateEcheance(nouvelleEcheance);
        }
    };

    const navigate = useNavigate();

    const enterLoading = () => setLoading(true);
    const outLoading = () => setLoading(false);

    const handleReportEffetSubmit = async () => {
        try {
            await form.validateFields();

            // Vérifications d'éligibilité
            if (!isContratEligible()) {
                toast.error("Ce contrat n'est pas éligible au report d'effet");
                return;
            }

            if (!nouvelleDateEffet || !nouvelleDateEcheance) {
                toast.error("Veuillez sélectionner une nouvelle date d'effet");
                return;
            }

            enterLoading();

            const payload = {
                contratCodeId: contratActuel.codeId,
                ancienneDateEffet: ancienneDateEffet.format('YYYY-MM-DD'),
                ancienneDateEcheance: ancienneDateEcheance.format('YYYY-MM-DD'),
                dateEffet: nouvelleDateEffet.format('YYYY-MM-DD'),
                dateEcheance: nouvelleDateEcheance.format('YYYY-MM-DD'),
                dureeInitiale,
                accessoires: fraisAccessoires,
                taxes: fraisAccessoires * 0.06 || 0,
                primeNette: 0,
                primeTotale: fraisAccessoires * 1.06
            };

            console.log("Payload report d'effet:", payload);

            const response = await axiosInstance.put(
                `/api/contrat/report-effet`,
                JSON.stringify(payload)
            );

            toast.success("Report d'effet effectué avec succès!");
            setModalReportEffet(false);
            props.update(false);
            navigate(`/details-contrat/${response.data.codeId}`, { replace: true });
            window.location.reload();

        } catch (error) {
            console.error(error);
            toast.error("Erreur lors du report d'effet");
        } finally {
            outLoading();
        }
    };

    const handleCancel = () => {
        setModalReportEffet(false);
        props.update(false);
    };

    const isContratEligible = () => {
        // Date d'effet doit être à venir
        const dateEffetAvenir = ancienneDateEffet && ancienneDateEffet.isAfter(dayjs());
        // Doit être une affaire nouvelle ou un renouvellement
        const typeValide = contratActuel?.avenant?.code === 'AN' ||
            contratActuel?.avenant?.code === 'RN';

        return dateEffetAvenir && typeValide;
    };

    const calculateNombreJoursReport = () => {
        if (!ancienneDateEffet || !nouvelleDateEffet) return 0;
        return nouvelleDateEffet.diff(ancienneDateEffet, 'day');
    };

    return (
        <SDIModal
            title="REPORT D'EFFET DE CONTRAT"
            centered
            confirmLoading={loading}
            open={modalReportEffet}
            onOk={handleReportEffetSubmit}
            onCancel={handleCancel}
            width={1000}
            zIndex={1940}
            okText="Effectuer le report"
            cancelText="Annuler"
        >
            <ConfigProvider locale={frFR}>
                <div className="report-effet-content">
                    <AvenantAgencySelector
                        organismeCode={props?.contrat?.organisme?.code}
                        defaultAgence={props?.contrat?.producteur?.agence || props?.contrat?.intermediaire?.agence}
                    />

                    {/* Informations sur le contrat actuel */}
                    {/* {contratActuel && (
                        <Card className="mb-4" size="small" title="Contrat à reporter">
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Statistic
                                        title="Numéro de police"
                                        value={contratActuel?.numeroPoliceP || contratActuel?.numeroPoliceM || 'N/A'}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Type de contrat"
                                        value={contratActuel.typeContrat || contratActuel.typeAvenant || 'N/A'}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Durée initiale"
                                        value={`${Math.round(dureeInitiale)} mois`}
                                        prefix={<ClockCircleOutlined />}
                                    />
                                </Col>
                            </Row>
                        </Card>
                    )} */}

                    {/* Conditions d'éligibilité */}
                    {!isContratEligible() ? (
                        <Alert
                            message="Contrat non éligible au report d'effet"
                            description={
                                !ancienneDateEffet?.isAfter(dayjs())
                                    ? "Le report d'effet n'est possible que sur les contrats dont la date d'effet est à venir."
                                    : "Le report d'effet n'est possible que sur les affaires nouvelles et les avenants de renouvellement."
                            }
                            type="error"
                            icon={<WarningOutlined />}
                            showIcon
                            className="mb-4"
                        />
                    ) : null}

                    {/* Informations sur le report d'effet */}
                    {/* <Card className="mb-4" size="small" title="Règles du report d'effet">
                        <div className="info-section">
                            <div className="info-item">
                                <InfoCircleOutlined className="info-icon info" />
                                <Text>
                                    <Text strong>Code avenant :</Text> RE + Année + 001 (premier report d'effet de l'année)
                                </Text>
                            </div>
                            <div className="info-item">
                                <CalendarOutlined className="info-icon warning" />
                                <Text>
                                    <Text strong>Durée préservée :</Text> La durée totale de couverture reste inchangée
                                </Text>
                            </div>
                            <div className="info-item">
                                <ClockCircleOutlined className="info-icon success" />
                                <Text>
                                    <Text strong>Échéance décalée :</Text> L'échéance est automatiquement recalculée
                                </Text>
                            </div>
                        </div>
                    </Card> */}
                    {isContratEligible() && (
                        <Form form={form} layout="vertical">
                            {/* Comparaison des dates */}
                            <Card className="mb-4" size="small" title="Comparaison des dates">
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <div className="date-comparison-section">
                                            <Title level={5} className="section-title">Dates actuelles</Title>
                                            <div className="date-item">
                                                <Text strong>Date d'effet actuelle:</Text>
                                                <Text className="ml-2 text-orange-600">
                                                    {ancienneDateEffet ? ancienneDateEffet.format('DD/MM/YYYY HH:mm:ss') : 'N/A'}
                                                </Text>
                                            </div>
                                            <div className="date-item">
                                                <Text strong>Date d'échéance actuelle:</Text>
                                                <Text className="ml-2 text-orange-600">
                                                    {ancienneDateEcheance ? ancienneDateEcheance.format('DD/MM/YYYY HH:mm:ss') : 'N/A'}
                                                </Text>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className="date-comparison-section">
                                            <Title level={5} className="section-title">Nouvelles dates</Title>
                                            <div className="date-item">
                                                <Text strong>Nouvelle date d'effet:</Text>
                                                <Text className="ml-2 text-green-600">
                                                    {nouvelleDateEffet ? nouvelleDateEffet.format('DD/MM/YYYY HH:mm:ss') : 'À définir'}
                                                </Text>
                                            </div>
                                            <div className="date-item">
                                                <Text strong>Nouvelle date d'échéance:</Text>
                                                <Text className="ml-2 text-green-600">
                                                    {nouvelleDateEcheance ? nouvelleDateEcheance.format('DD/MM/YYYY HH:mm:ss') : 'Auto-calculée'}
                                                </Text>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card>

                            {/* Sélection de la nouvelle date d'effet */}
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="Nouvelle date d'effet"
                                        name="nouvelleDateEffet"
                                        rules={[{ required: true, message: 'Veuillez sélectionner une nouvelle date d\'effet' }]}
                                    >
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            format="DD/MM/YYYY"
                                            value={nouvelleDateEffet}
                                            onChange={handleNouvelleDateEffetChange}
                                            disabledDate={(current) => {
                                                // Date doit être postérieure à aujourd'hui
                                                return current && current <= dayjs().startOf('day');
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Nouvelle date d'échéance (calculée automatiquement)">
                                        <Input
                                            value={nouvelleDateEcheance ? nouvelleDateEcheance.format('DD/MM/YYYY') : ''}
                                            disabled
                                            style={{ backgroundColor: '#f0f8f0' }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* Informations sur le report */}
                            {nouvelleDateEffet && ancienneDateEffet && (
                                <Card className="mb-4" size="small" title="Détails du report">
                                    <Row gutter={16}>
                                        <Col span={8}>
                                            <Statistic
                                                title="Nombre de jours reportés"
                                                value={calculateNombreJoursReport()}
                                                suffix="jours"
                                                valueStyle={{ color: '#1890ff' }}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Durée de couverture maintenue"
                                                value={Math.round(dureeInitiale)}
                                                suffix="mois"
                                                valueStyle={{ color: '#52c41a' }}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Type d'avenant"
                                                value={`RE${new Date().getFullYear()}001`}
                                                valueStyle={{ color: '#722ed1' }}
                                            />
                                        </Col>
                                    </Row>
                                </Card>
                            )}

                            {/* Motif du report */}
                            {/* <Form.Item
                            label="Motif du report d'effet"
                            name="motifReport"
                            rules={[{ required: true, message: 'Veuillez indiquer le motif du report' }]}
                        >
                            <TextArea
                                value={motifReport}
                                onChange={(e) => setMotifReport(e.target.value)}
                                placeholder="Indiquez la raison du report d'effet (ex: demande de l'assuré, report pour convenance, etc.)"
                                rows={3}
                            />
                        </Form.Item> */}

                            {/* Récapitulatif financier */}
                            <Card className="cost-summary" size="small">
                                <Title level={5}>
                                    <DollarOutlined className="mr-2" />
                                    Récapitulatif financier du report
                                </Title>
                                <div className="cost-breakdown">
                                    <Row justify="space-between" className="cost-line">
                                        <Col>
                                            <Text>Prime d'assurance:</Text>
                                        </Col>
                                        <Col>
                                            <Text strong className="text-green-600">
                                                {formatCurrency(0)} (Aucune prime générée)
                                            </Text>
                                        </Col>
                                    </Row>

                                    <Row justify="space-between" className="cost-line">
                                        <Col>
                                            <Text>Ristourne:</Text>
                                        </Col>
                                        <Col>
                                            <Text strong className="text-green-600">
                                                {formatCurrency(0)} (Aucune ristourne)
                                            </Text>
                                        </Col>
                                    </Row>

                                    <Row justify="space-between" className="cost-line">
                                        <Col>
                                            <Text>Frais accessoires:</Text>
                                        </Col>
                                        <Col>
                                            <Text strong>{formatCurrency(fraisAccessoires)}</Text>
                                        </Col>
                                    </Row>
                                    <Row justify="space-between" className="cost-line">
                                        <Col>
                                            <Text>Taxes:</Text>
                                        </Col>
                                        <Col>
                                            <Text strong>{formatCurrency(fraisAccessoires * 0.06)}</Text>
                                        </Col>
                                    </Row>
                                    <Divider className="my-2" />

                                    <Row justify="space-between" className="cost-line total-cost">
                                        <Col>
                                            <Text strong className="text-lg">MONTANT TOTAL À PAYER:</Text>
                                        </Col>
                                        <Col>
                                            <Text strong className="text-lg text-blue-600">
                                                {formatCurrency(fraisAccessoires * 1.06)}
                                            </Text>
                                        </Col>
                                    </Row>
                                </div>
                            </Card>
                        </Form>
                    )}

                </div>
            </ConfigProvider>

            <style jsx>
                {`
                    .report-effet-content {
                        max-height: 70vh;
                        overflow-y: auto;
                    }
                    
                    .info-section {
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }
                    
                    .info-item {
                        display: flex;
                        align-items: flex-start;
                        gap: 8px;
                    }
                    
                    .info-icon {
                        margin-top: 2px;
                        font-size: 16px;
                    }
                    
                    .info-icon.warning {
                        color: #faad14;
                    }
                    
                    .info-icon.info {
                        color: #1890ff;
                    }
                    
                    .info-icon.success {
                        color: #52c41a;
                    }
                    
                    .date-comparison-section {
                        padding: 16px;
                        background-color: #fafafa;
                        border-radius: 6px;
                        height: 100%;
                    }
                    
                    .section-title {
                        margin-bottom: 12px !important;
                        color: #1890ff;
                    }
                    
                    .date-item {
                        margin-bottom: 8px;
                        display: flex;
                        flex-direction: column;
                    }
                    
                    .cost-summary {
                        background-color: #f8f9fa;
                        border: 1px solid #e9ecef;
                    }
                    
                    .cost-breakdown .cost-line {
                        margin-bottom: 8px;
                    }
                    
                    .cost-breakdown .total-cost {
                        background-color: #e6f7ff;
                        padding: 8px;
                        border-radius: 4px;
                        margin-top: 8px;
                    }
                    
                    .ml-2 { margin-left: 8px; }
                    .mr-2 { margin-right: 8px; }
                    .mb-4 { margin-bottom: 16px; }
                    .my-2 { margin: 8px 0; }
                    .text-lg { font-size: 16px; }
                    .text-blue-600 { color: #1890ff; }
                    .text-green-600 { color: #52c41a; }
                    .text-orange-600 { color: #fa8c16; }
                `}
            </style>
        </SDIModal>
    );
};

ReportEffet.propTypes = {
    open: PropTypes.bool,
    update: PropTypes.func,
    contrat: PropTypes.object
};

export default ReportEffet;
